import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecentEntriesPanel = ({ entries, onUndo }) => {
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'validated':
        return 'text-success';
      case 'duplicate':
        return 'text-warning-foreground';
      case 'error':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'validated':
        return 'CheckCircle2';
      case 'duplicate':
        return 'AlertTriangle';
      case 'error':
        return 'XCircle';
      default:
        return 'Clock';
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 border border-border h-full flex flex-col">
      <div className="px-24 md:px-32 py-24 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-12">
            <div className="w-[40px] h-[40px] bg-secondary/10 rounded-lg flex items-center justify-center">
              <Icon name="History" size={20} color="var(--color-secondary)" strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
                Recent Entries
              </h3>
              <p className="font-caption text-xs text-muted-foreground">
                Last 20 submissions
              </p>
            </div>
          </div>
          <div className="flex items-center gap-8 px-12 py-6 bg-muted rounded-lg">
            <Icon name="Package" size={14} color="var(--color-muted-foreground)" strokeWidth={2} />
            <span className="font-mono font-semibold text-sm text-foreground">
              {entries?.length}
            </span>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-18 md:p-24 space-y-12">
        {entries?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-48 text-center">
            <div className="w-[64px] h-[64px] bg-muted rounded-full flex items-center justify-center mb-18">
              <Icon name="Package" size={32} color="var(--color-muted-foreground)" strokeWidth={2} />
            </div>
            <p className="font-body text-sm md:text-base text-muted-foreground">
              No entries yet
            </p>
            <p className="font-caption text-xs text-muted-foreground mt-6">
              Start by entering a tracking number
            </p>
          </div>
        ) : (
          entries?.map((entry, index) => (
            <div
              key={entry?.id}
              className="bg-background rounded-lg p-18 border border-border hover:border-primary/30 transition-smooth group"
            >
              <div className="flex items-start justify-between gap-12 mb-12">
                <div className="flex items-center gap-12 flex-1 min-w-0">
                  <Icon
                    name={getStatusIcon(entry?.status)}
                    size={18}
                    color={`var(--color-${entry?.status === 'validated' ? 'success' : entry?.status === 'duplicate' ? 'warning' : 'error'})`}
                    strokeWidth={2}
                    className="flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-xs md:text-sm text-foreground font-medium truncate">
                      {entry?.trackingNumber}
                    </p>
                    <p className="font-caption text-xs text-muted-foreground">
                      {entry?.carrier}
                    </p>
                  </div>
                </div>
                {index === 0 && (
                  <Button
                    variant="ghost"
                    size="xs"
                    iconName="Undo2"
                    onClick={() => onUndo(entry?.entryId)}
                    className="opacity-0 group-hover:opacity-100 transition-smooth"
                  >
                    Undo
                  </Button>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="font-caption text-xs text-muted-foreground">
                  {formatTime(entry?.timestamp)}
                </span>
                <span className={`font-caption text-xs font-medium ${getStatusColor(entry?.status)}`}>
                  {entry?.status?.charAt(0)?.toUpperCase() + entry?.status?.slice(1)}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="px-24 md:px-32 py-18 border-t border-border bg-muted/50">
        <div className="flex items-center justify-between text-xs md:text-sm">
          <div className="flex items-center gap-8">
            <Icon name="CheckCircle2" size={14} color="var(--color-success)" strokeWidth={2} />
            <span className="font-caption text-muted-foreground">
              {entries?.filter(e => e?.status === 'validated')?.length} Validated
            </span>
          </div>
          <div className="flex items-center gap-8">
            <Icon name="AlertTriangle" size={14} color="var(--color-warning-foreground)" strokeWidth={2} />
            <span className="font-caption text-muted-foreground">
              {entries?.filter(e => e?.status === 'duplicate')?.length} Duplicates
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecentEntriesPanel;